import React, { useEffect, useRef, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { colors, typography } from '../../../styles/theme';
import { FILTER_DROPDOWN_HEIGHT, FILTER_ACCENT } from './FilterPanel.styles';

/** Cada emisión reconstruye la expresión y repinta las capas: no por tecla. */
const DEBOUNCE_MS = 300;

const wrapStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  width: '100%',
};

const labelStyle = (isActive) => ({
  fontSize: typography.fontSize.sm,
  fontFamily: typography.fontFamily.primary,
  fontWeight: typography.fontWeight.medium,
  color: colors.textPanelDescription,
  opacity: isActive ? 1 : 0.75,
});

const fieldStyle = ({ isActive = false, disabled = false } = {}) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  width: '100%',
  height: `${FILTER_DROPDOWN_HEIGHT}px`,
  boxSizing: 'border-box',
  padding: '0 8px 0 12px',
  borderRadius: '6px',
  border: `1px solid ${isActive ? FILTER_ACCENT : colors.border}`,
  backgroundColor: colors.backgroundSurface,
  opacity: disabled ? 0.7 : 1,
});

const inputStyle = (disabled) => ({
  flex: 1,
  minWidth: 0,
  border: 'none',
  outline: 'none',
  background: 'transparent',
  color: disabled ? 'rgba(255,255,255,0.35)' : colors.textPanelDescription,
  fontSize: typography.fontSize.sm,
  fontFamily: typography.fontFamily.primary,
  cursor: disabled ? 'not-allowed' : 'text',
});

const clearButtonStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 0,
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
};

const hintStyle = {
  fontSize: typography.fontSize.xs,
  fontFamily: typography.fontFamily.primary,
  color: colors.textSecondary,
};

/**
 * Filtro de texto "contiene", con debounce.
 * Emite `null` mientras no se alcance `minLength`, para no filtrar con 1–2
 * letras (a zoom bajo eso deja casi todas las parcelas encendidas).
 *
 * @param {string|null} value texto aplicado; null si no hay filtro
 * @param {string} [hint] nota al pie; también explica por qué está deshabilitado
 */
function FilterTextInput({
  label,
  value = null,
  placeholder = '',
  minLength = 3,
  hint = null,
  disabled = false,
  onChange,
}) {
  const [draft, setDraft] = useState(value || '');
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  // Último valor emitido: distingue un cambio propio de uno externo (chip
  // removido, "Limpiar"), que sí debe sobreescribir lo tipeado.
  const lastEmittedRef = useRef(value || null);

  useEffect(() => {
    const applied = value || null;
    if (applied === lastEmittedRef.current) return;
    lastEmittedRef.current = applied;
    setDraft(applied || '');
  }, [value]);

  useEffect(() => {
    const trimmed = draft.trim().replace(/\s+/g, ' ');
    const next = trimmed.length >= minLength ? trimmed : null;
    if (next === lastEmittedRef.current) return undefined;

    const timer = setTimeout(() => {
      lastEmittedRef.current = next;
      onChangeRef.current?.(next);
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [draft, minLength]);

  const isActive = Boolean(value);
  const isTooShort = draft.trim().length > 0 && draft.trim().length < minLength;
  const footnote = isTooShort ? `Mínimo ${minLength} caracteres` : hint;

  return (
    <div style={wrapStyle}>
      <span style={labelStyle(isActive)}>{label}</span>
      <div style={fieldStyle({ isActive, disabled })}>
        <input
          type="text"
          value={draft}
          placeholder={placeholder}
          disabled={disabled}
          aria-label={label}
          style={inputStyle(disabled)}
          onChange={(event) => setDraft(event.target.value)}
        />
        {draft.length > 0 && !disabled && (
          <button
            type="button"
            style={clearButtonStyle}
            aria-label={`Quitar filtro de ${label.toLowerCase()}`}
            onClick={() => setDraft('')}
          >
            <CloseIcon sx={{ fontSize: 16, color: colors.textSecondary }} />
          </button>
        )}
      </div>
      {footnote && <span style={hintStyle}>{footnote}</span>}
    </div>
  );
}

export default FilterTextInput;
